import { useCallback, useEffect, useRef } from 'react';
import { apiClient } from '../services/api';
import type { CommunicationMode } from '../types/app';

/**
 * W5: reports the customer's current mode to Portal, so the seller can be told.
 *
 * Two things stand between the tab bar and the network, and both are the point
 * of this hook:
 *
 * - **Coalescing.** A customer flicking through the tabs produces a mode change
 *   per tap. Only the mode they SETTLE on is a fact about the meeting; the ones
 *   they passed through are not, and each one would put a card on the seller's
 *   screen. So the send is delayed and the pending value replaced — the last
 *   mode within the window is the one that goes.
 * - **No no-ops.** A mode identical to the last one we sent is dropped here
 *   rather than at the server. Portal also refuses to broadcast an unchanged
 *   mode, but a switch away and back within the window must not produce a
 *   request at all.
 *
 * The report is deliberately downstream of ConsultationScreen's `onModeChange`
 * rather than inside its `handleModeChange`: at that point the mode is still
 * optimistic and may yet be rolled back by a failed connect (S3). `onModeChange`
 * fires on the mode the customer actually ended up in.
 */
export const MODE_REPORT_DEBOUNCE_MS = 500;

export function useModeReporter(roomKey: string | null): (mode: CommunicationMode) => void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<CommunicationMode | null>(null);
  const pendingRef = useRef<CommunicationMode | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  return useCallback((mode: CommunicationMode) => {
    if (!roomKey) return;
    // Already reported and nothing pending — nothing happened worth sending.
    if (pendingRef.current === null && lastSentRef.current === mode) return;

    pendingRef.current = mode;

    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const settled = pendingRef.current;
      pendingRef.current = null;
      if (settled === null || settled === lastSentRef.current) return;

      lastSentRef.current = settled;
      void apiClient.reportParticipantMode(roomKey, settled);
    }, MODE_REPORT_DEBOUNCE_MS);
  }, [roomKey]);
}
